/**
 * Phase 7E AC19 — Schedule-of-Activities matrix editor.
 *
 * Route: /studies/:studyId/schedule
 *
 * Grid where rows are visit templates (Screening, Baseline, Follow-up …)
 * and columns are procedures visible on the study. Each cell is a
 * checkbox: checked = the procedure is required at that visit.
 *
 * Row header carries the visit config: name, order, day offset (signed —
 * negative for Screening), tolerance window, is-required flag, and the
 * "Include standard vitals" bundle toggle.
 *
 * Save semantics:
 *   - Row-level edits (name, day_offset, window, flags) auto-save
 *     against the visit's PATCH endpoint on blur.
 *   - Procedure assignment cells batch into a per-row PUT that
 *     wholesale-replaces the row's assignment set (deletes-then-inserts
 *     server-side).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2,
  Plus,
  Save,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  procedures,
  schedule,
  type VisitTemplateRow,
} from '@/api/client';
import { useHasRole } from '@/components/RoleGate';
import { errorText } from '@/lib/permissionError';

const EDIT_ROLES = [
  'site_study_designer',
  'regulatory_specialist',
] as const;

interface RowState {
  visit_name: string;
  day_offset: number;
  window_minus_days: number;
  window_plus_days: number;
  is_required: boolean;
  include_standard_vitals: boolean;
  procedure_ids: Set<string>;
  dirty: boolean;
  saving: boolean;
}

function initRowState(
  v: VisitTemplateRow,
  procedureIds: string[],
): RowState {
  return {
    visit_name: v.visit_name,
    day_offset: v.nominal_day,
    window_minus_days: v.window_minus_days,
    window_plus_days: v.window_plus_days,
    is_required: (v.is_required ?? 'true') !== 'false',
    include_standard_vitals: (v.include_standard_vitals ?? 'false') === 'true',
    procedure_ids: new Set(procedureIds),
    dirty: false,
    saving: false,
  };
}

export function StudyScheduleMatrixPage() {
  const { studyId } = useParams<{ studyId: string }>();
  const canEdit = useHasRole(EDIT_ROLES);
  const qc = useQueryClient();

  const scheduleQ = useQuery({
    queryKey: ['study', 'schedule', studyId],
    queryFn: () => schedule.get(studyId!),
    enabled: !!studyId,
  });
  const proceduresQ = useQuery({
    queryKey: ['procedures', 'for-schedule', studyId],
    queryFn: () => procedures.list({ study_id: studyId, active_only: true }),
    enabled: !!studyId,
    staleTime: 60_000,
  });

  const visits = scheduleQ.data?.visits ?? [];
  const assignments = scheduleQ.data?.assignments ?? [];
  const procedureRows = proceduresQ.data?.rows ?? [];

  // Row state — local drafts per visit_template_id keyed by row.
  const [rows, setRows] = useState<Map<string, RowState>>(new Map());

  // Hydrate row state from server whenever visits/assignments change.
  useMemo(() => {
    const next = new Map<string, RowState>();
    for (const v of visits) {
      const procIds = assignments
        .filter((a) => a.visit_template_id === v.template_visit_id)
        .map((a) => a.procedure_id);
      next.set(v.template_visit_id, initRowState(v, procIds));
    }
    setRows(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleQ.data]);

  function updateRow(id: string, patch: Partial<RowState>) {
    setRows((prev) => {
      const copy = new Map(prev);
      const cur = copy.get(id);
      if (!cur) return prev;
      copy.set(id, { ...cur, ...patch, dirty: true });
      return copy;
    });
  }

  function toggleProc(id: string, procId: string, on: boolean) {
    setRows((prev) => {
      const copy = new Map(prev);
      const cur = copy.get(id);
      if (!cur) return prev;
      const next = new Set(cur.procedure_ids);
      if (on) next.add(procId);
      else next.delete(procId);
      copy.set(id, { ...cur, procedure_ids: next, dirty: true });
      return copy;
    });
  }

  const create = useMutation({
    mutationFn: (body: { visit_name: string; day_offset: number }) =>
      schedule.createVisit(studyId!, body),
    onSuccess: () => {
      toast.success('Visit added.');
      qc.invalidateQueries({ queryKey: ['study', 'schedule', studyId] });
    },
    onError: (e) => toast.error(errorText(e, 'Add failed')),
  });
  const remove = useMutation({
    mutationFn: (templateVisitId: string) =>
      schedule.deleteVisit(studyId!, templateVisitId),
    onSuccess: () => {
      toast.success('Visit removed.');
      qc.invalidateQueries({ queryKey: ['study', 'schedule', studyId] });
    },
    onError: (e) => toast.error(errorText(e, 'Remove failed')),
  });

  async function saveRow(v: VisitTemplateRow, state: RowState) {
    setRows((prev) => {
      const copy = new Map(prev);
      const cur = copy.get(v.template_visit_id);
      if (cur) copy.set(v.template_visit_id, { ...cur, saving: true });
      return copy;
    });
    try {
      await schedule.updateVisit(studyId!, v.template_visit_id, {
        visit_name: state.visit_name,
        day_offset: state.day_offset,
        window_minus_days: state.window_minus_days,
        window_plus_days: state.window_plus_days,
        is_required: state.is_required,
        include_standard_vitals: state.include_standard_vitals,
      });
      await schedule.putAssignments(studyId!, v.template_visit_id, {
        procedure_ids: Array.from(state.procedure_ids),
      });
      toast.success(`Saved ${state.visit_name}.`);
      setRows((prev) => {
        const copy = new Map(prev);
        const cur = copy.get(v.template_visit_id);
        if (cur) copy.set(v.template_visit_id, { ...cur, dirty: false, saving: false });
        return copy;
      });
      qc.invalidateQueries({ queryKey: ['study', 'schedule', studyId] });
    } catch (e) {
      setRows((prev) => {
        const copy = new Map(prev);
        const cur = copy.get(v.template_visit_id);
        if (cur) copy.set(v.template_visit_id, { ...cur, saving: false });
        return copy;
      });
      toast.error(errorText(e, 'Save failed'));
    }
  }

  const [newVisitName, setNewVisitName] = useState('');
  const [newDayOffset, setNewDayOffset] = useState<number>(0);

  if (!studyId) {
    return (
      <div className="p-10 text-center text-sm text-danger-600">
        Study id required.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <PageHeader
        backTo={`/studies/${studyId}`}
        title="Schedule of Activities"
        subtitle="Visits × procedures matrix. Day offsets are signed (Screening −35 to −1, Day 0, follow-up +N); tolerance window defines the in-window band."
      />

      <div className="card overflow-x-auto p-0">
        {scheduleQ.isLoading || proceduresQ.isLoading ? (
          <div className="p-8 text-center text-xs text-ink-muted">
            <Loader2 className="mx-auto h-4 w-4 animate-spin" /> Loading matrix…
          </div>
        ) : visits.length === 0 ? (
          <div className="p-8 text-center text-xs text-ink-muted">
            No visits configured yet — add the first one below.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-surface-muted/40">
              <tr>
                <th className="sticky left-0 z-10 min-w-[280px] border-b border-r border-surface-border bg-surface-muted/40 px-2 py-2 text-left font-semibold">
                  Visit
                </th>
                <th className="border-b border-surface-border px-2 py-2 text-center font-semibold">
                  Vitals
                </th>
                {procedureRows.map((p) => (
                  <th
                    key={p.procedure_id}
                    className="border-b border-l border-surface-border px-2 py-2 text-center font-medium"
                    title={p.description}
                  >
                    <div className="text-[10px] leading-tight">{p.label}</div>
                    <div className="text-[9px] font-mono text-ink-muted">{p.code}</div>
                  </th>
                ))}
                <th className="border-b border-l border-surface-border px-2 py-2 text-center font-semibold">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {visits.map((v) => {
                const state = rows.get(v.template_visit_id);
                if (!state) return null;
                return (
                  <tr key={v.template_visit_id} className="hover:bg-surface-muted/20">
                    <td className="sticky left-0 z-10 min-w-[280px] border-r border-surface-border bg-surface-card px-2 py-1.5">
                      <VisitRowConfig
                        state={state}
                        onChange={(patch) => updateRow(v.template_visit_id, patch)}
                        readOnly={!canEdit}
                      />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={state.include_standard_vitals}
                        onChange={(e) =>
                          updateRow(v.template_visit_id, {
                            include_standard_vitals: e.target.checked,
                          })
                        }
                        disabled={!canEdit}
                        title="Include standard vitals (height/weight/BP/temp) on this visit"
                      />
                    </td>
                    {procedureRows.map((p) => (
                      <td key={p.procedure_id} className="px-2 py-1.5 text-center">
                        <input
                          type="checkbox"
                          checked={state.procedure_ids.has(p.procedure_id)}
                          onChange={(e) =>
                            toggleProc(v.template_visit_id, p.procedure_id, e.target.checked)
                          }
                          disabled={!canEdit}
                        />
                      </td>
                    ))}
                    <td className="whitespace-nowrap px-2 py-1.5 text-center">
                      {canEdit && (
                        <>
                          <button
                            type="button"
                            onClick={() => saveRow(v, state)}
                            disabled={!state.dirty || state.saving}
                            className="mr-1 rounded border border-surface-border px-1.5 py-0.5 text-[10px] font-medium hover:bg-surface-muted/50 disabled:opacity-40"
                          >
                            <Save className="mr-0.5 inline h-2.5 w-2.5" />
                            {state.saving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Delete visit "${state.visit_name}"? Its procedure assignments are also removed.`,
                                )
                              ) {
                                remove.mutate(v.template_visit_id);
                              }
                            }}
                            className="rounded p-1 text-danger-600 hover:bg-danger-50"
                          >
                            <Trash2 className="h-2.5 w-2.5" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {canEdit && (
        <div className="card p-3">
          <p className="mb-2 text-xs font-semibold text-ink-primary">Add visit</p>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs">
              <span className="block font-medium text-ink-secondary">Name</span>
              <input
                type="text"
                value={newVisitName}
                onChange={(e) => setNewVisitName(e.target.value)}
                placeholder="Screening / Baseline / Week 4 …"
                className="mt-1 rounded border border-surface-border px-2 py-1"
              />
            </label>
            <label className="text-xs">
              <span className="block font-medium text-ink-secondary">Day offset</span>
              <input
                type="number"
                value={newDayOffset}
                onChange={(e) => setNewDayOffset(Number(e.target.value))}
                className="mt-1 w-24 rounded border border-surface-border px-2 py-1"
              />
            </label>
            <button
              type="button"
              onClick={() => {
                if (!newVisitName.trim()) return;
                create.mutate({
                  visit_name: newVisitName.trim(),
                  day_offset: newDayOffset,
                });
                setNewVisitName('');
                setNewDayOffset(0);
              }}
              disabled={!newVisitName.trim() || create.isPending}
              className="rounded bg-accent-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-700 disabled:opacity-50"
            >
              <Plus className="mr-1 inline h-3 w-3" />
              {create.isPending ? 'Adding…' : 'Add visit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function VisitRowConfig({
  state,
  onChange,
  readOnly,
}: {
  state: RowState;
  onChange: (patch: Partial<RowState>) => void;
  readOnly: boolean;
}) {
  return (
    <div className="space-y-1">
      <input
        type="text"
        value={state.visit_name}
        onChange={(e) => onChange({ visit_name: e.target.value })}
        disabled={readOnly}
        className="w-full rounded border border-surface-border px-2 py-0.5 text-xs font-medium disabled:opacity-70"
      />
      <div className="flex items-center gap-1 text-[10px]">
        <label className="flex items-center gap-1">
          <span className="text-ink-muted">Day</span>
          <input
            type="number"
            value={state.day_offset}
            onChange={(e) => onChange({ day_offset: Number(e.target.value) })}
            disabled={readOnly}
            className="w-14 rounded border border-surface-border px-1 py-0.5 disabled:opacity-70"
          />
        </label>
        <label className="flex items-center gap-1">
          <span className="text-ink-muted">±</span>
          <input
            type="number"
            min={0}
            value={state.window_minus_days}
            onChange={(e) => onChange({ window_minus_days: Number(e.target.value) })}
            disabled={readOnly}
            className="w-10 rounded border border-surface-border px-1 py-0.5 disabled:opacity-70"
          />
          <span>/</span>
          <input
            type="number"
            min={0}
            value={state.window_plus_days}
            onChange={(e) => onChange({ window_plus_days: Number(e.target.value) })}
            disabled={readOnly}
            className="w-10 rounded border border-surface-border px-1 py-0.5 disabled:opacity-70"
          />
        </label>
        <label className="flex items-center gap-1">
          <input
            type="checkbox"
            checked={state.is_required}
            onChange={(e) => onChange({ is_required: e.target.checked })}
            disabled={readOnly}
          />
          <span>Req'd</span>
        </label>
        {state.dirty && !state.saving && (
          <span className="ml-auto rounded bg-amber-100 px-1 py-0.5 text-[9px] text-amber-700">
            Unsaved
          </span>
        )}
      </div>
    </div>
  );
}
